import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, RotateCcw, ShieldCheck, LogOut } from "lucide-react";
import { toast } from "sonner";
import LevelGrid from "./LevelGrid";
import { LevelLegend } from "./PresetManager";
import AccessSummary from "./AccessSummary";
import api from "../../lib/api";
import { ACCESS_ROLE_LABELS, ACCESS_ROLE_ORDER, fetchAccessUser, patchAccessUser, diffFromPreset } from "../../lib/access";

const allManage = (groups) => Object.fromEntries(groups.flatMap((g) => g.children.map((c) => [c.page_id, "manage"])));

export default function AccessDrawer({ user, groups, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [lv, setLv] = useState({});
  const [capOv, setCapOv] = useState({});
  const [saving, setSaving] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const role = detail?.access_role || "";
  const isAdmin = detail && (detail.role === "admin" || detail.super_admin);
  const locked = !role || isAdmin;
  const base = useMemo(() => (role === "view_only" ? {} : detail?.preset_levels || {}), [detail, role]);

  const load = async () => {
    const d = await fetchAccessUser(user.id);
    setDetail(d);
    setLv(d.effective_levels || allManage(groups));
    setCapOv(Object.fromEntries((d.capability_overrides || []).map((o) => [o.key, o.granted])));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user.id]);

  const dirty = useMemo(() => {
    if (!detail || locked) return false;
    const a = JSON.stringify(Object.entries(lv).sort()), b = JSON.stringify(Object.entries(detail.effective_levels || {}).sort());
    const c = JSON.stringify(Object.entries(capOv).sort()), d = JSON.stringify((detail.capability_overrides || []).map((o) => [o.key, o.granted]).sort());
    return a !== b || c !== d;
  }, [lv, capOv, detail, locked]);

  const applyRoleChange = async (newRole, keep) => {
    setSaving(true);
    try {
      await patchAccessUser(user.id, { access_role: newRole, keep_overrides: keep });
      toast.success(`Role set to ${ACCESS_ROLE_LABELS[newRole]}`);
      setPendingRole(null); await load(); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to change role"); }
    finally { setSaving(false); }
  };

  const forceLogout = async () => {
    if (!window.confirm(`Sign ${user.name || user.email} out on every device? They'll sign in again and get their current access.`)) return;
    try { await api.post(`/access/users/${user.id}/force-logout`); toast.success("Signed out everywhere"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not sign out"); }
  };

  const resetToPreset = async () => {
    if (!window.confirm(`Remove all custom access for ${user.name} and go back to the ${ACCESS_ROLE_LABELS[role]} preset?`)) return;
    setSaving(true);
    try { await patchAccessUser(user.id, { is_custom: false, capability_overrides: [] }); toast.success("Back to preset"); await load(); onSaved?.(); }
    catch { toast.error("Reset failed"); } finally { setSaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await patchAccessUser(user.id, {
        ...diffFromPreset(role, lv, detail.preset_levels),
        capability_overrides: Object.entries(capOv).map(([key, granted]) => ({ key, granted })),
      });
      toast.success("Access saved"); await load(); onSaved?.();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="access-drawer">
      <div className="w-full max-w-2xl h-full bg-[#0F1420] border-l border-white/10 shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <div className="w-9 h-9 rounded-full grid place-items-center font-display font-bold bg-gradient-to-br from-indigo-500 to-purple-600">{user.name?.[0]?.toUpperCase() || "?"}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{user.name}</div>
            <div className="text-[11px] text-slate-400 font-mono truncate">{user.email}</div>
          </div>
          <button onClick={forceLogout} title="Sign out on every device (forces fresh access)" className="inline-flex items-center gap-1 px-2 py-1.5 rounded border border-white/10 text-[11px] text-slate-300 hover:bg-white/5" data-testid="access-force-logout">
            <LogOut className="w-3.5 h-3.5" /> Force sign-out
          </button>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10 text-slate-300" data-testid="access-drawer-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-4 border-b border-white/10 space-y-3">
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-wider text-slate-400 w-12">Role</label>
            <select value={role} onChange={(e) => e.target.value && e.target.value !== role && setPendingRole({ role: e.target.value, keep: false })}
              className="input-dark flex-1" data-testid="access-role-select" disabled={!!detail?.super_admin}>
              {!role && <option value="">Unrestricted (no role yet)</option>}
              {ACCESS_ROLE_ORDER.map((r) => <option key={r} value={r}>{ACCESS_ROLE_LABELS[r]}</option>)}
            </select>
            {role && !isAdmin && detail?.is_custom && (
              <button onClick={resetToPreset} disabled={saving} className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-2 py-1.5 rounded border border-white/10 hover:bg-white/5" data-testid="access-reset-preset">
                <RotateCcw className="w-3 h-3" /> Reset to preset
              </button>
            )}
          </div>
          {isAdmin && (
            <div className="flex items-start gap-2 text-[11px] text-emerald-300/80 bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-3 py-2">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {detail?.super_admin ? "Super admin" : "Admin"}: Manage on every area, plus Config, user management and quote-owner changes.
            </div>
          )}
          {!role && <p className="text-[11px] text-sky-300/80">No role yet, so nothing is restricted. Pick a role to start controlling access.</p>}
          {role && !isAdmin && <p className="text-[11px] text-slate-500">Change any area for just this person. Anything different from the {ACCESS_ROLE_LABELS[role]} preset is marked <span className="text-fuchsia-300 font-mono">CUSTOM</span>.</p>}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {detail ? (<>
            <AccessSummary detail={detail} groups={groups} />
            <LevelLegend />
            <LevelGrid groups={groups} value={lv} base={locked ? null : base} onChange={setLv} locked={locked}
              abilities={isAdmin ? null : { mode: "user", value: capOv, effective: new Set(detail.capabilities || []), onChange: setCapOv, testid: "drawer-abilities" }} />
          </>) : <div className="text-slate-400 text-sm">Loading…</div>}
        </div>

        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-end gap-2">
          {dirty && <span className="mr-auto text-[11px] text-amber-300" data-testid="access-unsaved">Unsaved changes</span>}
          <button onClick={onClose} className="px-3 py-2 rounded-md text-sm border border-white/10 hover:bg-white/5">Close</button>
          <button onClick={save} disabled={saving || !dirty} className="btn-coral disabled:opacity-40 disabled:cursor-not-allowed" data-testid="access-save">{saving ? "Saving…" : "Save access"}</button>
        </div>
      </div>

      {pendingRole && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" onClick={() => setPendingRole(null)}>
          <div className="card-dark w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="role-change-confirm">
            <h3 className="font-display text-lg font-bold mb-2">Change role to {ACCESS_ROLE_LABELS[pendingRole.role]}?</h3>
            <p className="text-sm text-slate-300 mb-4">Access will switch to the {ACCESS_ROLE_LABELS[pendingRole.role]} preset.{pendingRole.role === "admin" && " Admins also get Config and user management."}</p>
            <label className="flex items-center gap-2 text-sm text-slate-300 mb-4 cursor-pointer">
              <input type="checkbox" checked={pendingRole.keep} onChange={(e) => setPendingRole((p) => ({ ...p, keep: e.target.checked }))} className="accent-orange-500 w-4 h-4" data-testid="role-change-keep-overrides" />
              Keep this person's custom access
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPendingRole(null)} className="px-3 py-2 rounded-md text-sm border border-white/10 hover:bg-white/5">Cancel</button>
              <button onClick={() => applyRoleChange(pendingRole.role, pendingRole.keep)} className="btn-coral" data-testid="role-change-confirm-btn">Change role</button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
